import { FlexColumn, FlexRow, LabeledInput, StyledButton, useLoading } from '@gorila-shared-ui/components';
import { useEffect, useMemo, useState } from 'react';
import { FEEDBACK, FEEDBACK_PREFIXES, SLUG_FIELD_ERROR_TEXT } from '../../../constants/app';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { createMaintenanceType, updateMaintenanceType } from '../../../services/maintenanceService';
import { MaintenanceType, MaintenanceTypeRequest } from '../../../types/maintenances';
import { isValidSlug } from '../../../utils/forms';

type Props = {
  maintenanceType?: MaintenanceType;
  onCancel: () => void;
  afterSave: () => void;
};

export function MaintenanceTypeForm({ maintenanceType, onCancel, afterSave }: Props) {
  const { css, theme } = useStyles();
  const [editingMaintenanceType, setEditingMaintenanceType] = useState<MaintenanceTypeRequest>({
    slug: '',
    value: '',
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const { startLoading, stopLoading, loading } = useLoading(false);
  const { showFailFeedback, showPositiveFeedback } = useFeedback();

  useEffect(() => {
    if (maintenanceType) {
      setEditingMaintenanceType({
        slug: maintenanceType.slug,
        value: maintenanceType.value,
      });
    }
  }, [maintenanceType]);

  const slugHasError =
    !slugTouched && editingMaintenanceType.slug === '' ? false : !isValidSlug(editingMaintenanceType.slug);

  const canSubmit = useMemo(() => {
    return !!editingMaintenanceType.value && !!editingMaintenanceType.slug && !slugHasError;
  }, [editingMaintenanceType, slugHasError]);

  const onInputChange = (value: string | undefined | null | {}, field: keyof MaintenanceType) => {
    setEditingMaintenanceType((prev) => ({ ...prev!, [field]: value }));
  };

  const onSave = async () => {
    startLoading();
    const { id, error } = maintenanceType
      ? await updateMaintenanceType(maintenanceType._id, editingMaintenanceType)
      : await createMaintenanceType(editingMaintenanceType);
    if (!error && id) {
      afterSave();
      showPositiveFeedback(
        maintenanceType
          ? FEEDBACK.edited(FEEDBACK_PREFIXES.maintenanceType, editingMaintenanceType.value)
          : FEEDBACK.created(FEEDBACK_PREFIXES.maintenanceType)
      );
    } else {
      showFailFeedback(
        error ||
          (maintenanceType
            ? FEEDBACK.failedEdition(FEEDBACK_PREFIXES.maintenanceType, editingMaintenanceType.value)
            : FEEDBACK.failedCreation(FEEDBACK_PREFIXES.maintenanceType))
      );
    }
    stopLoading();
  };

  return (
    <FlexColumn
      classNames={css({
        justifyContent: 'space-between',
        overflow: 'hidden',
        width: '100%',
        flexDirection: 'column',
        alignItems: 'end',
      })}
    >
      <FlexColumn
        classNames={css({
          overflow: 'auto',
          width: '100%',
          height: '100%',
          flexDirection: 'column',
        })}
      >
        <LabeledInput
          label="Nombre:"
          value={editingMaintenanceType?.value}
          onChange={(value) => onInputChange(value, 'value')}
          required
        />
        <LabeledInput
          label="Slug:"
          value={editingMaintenanceType?.slug}
          onChange={(value) => onInputChange(value, 'slug')}
          onBlur={() => {
            setSlugTouched(true);
          }}
          error={slugHasError && SLUG_FIELD_ERROR_TEXT}
          required
        />
      </FlexColumn>
      <FlexRow classNames={`${css({ alignItems: 'center', justifyContent: 'flex-end' })}`}>
        <FlexRow gap={theme.sizing.scale300}>
          {maintenanceType?._id && (
            <StyledButton
              kind="tertiary"
              onClick={onCancel}
            >
              Cancelar
            </StyledButton>
          )}
          <StyledButton
            onClick={onSave}
            isLoading={loading}
            disabled={!canSubmit}
          >
            Guardar
          </StyledButton>
        </FlexRow>
      </FlexRow>
    </FlexColumn>
  );
}
